import { Helmet } from "react-helmet";
import { OG_LOCALE, useLocale, type Locale } from "@/i18n";

const DEFAULT_OG_IMAGE = "https://farmly.se/farmly-og.png";
const SITE_NAME = "Farmly";
const DEFAULT_TWITTER_CARD = "summary_large_image";

export interface SeoProps {
  /** Page title - will be formatted as "{title} – Farmly" */
  title: string;
  /** Meta description (max ~160 chars) */
  description?: string;
  /** Canonical URL (full URL including domain) */
  canonicalUrl?: string;
  /** Open Graph type (website, product, article, etc.) */
  ogType?: "website" | "product" | "article" | "place";
  /** Primary image URL for OG/Twitter (full URL or path) */
  image?: string | null;
  /** Image alt text */
  imageAlt?: string;
  /** Should this page be indexed? Set to false for drafts/previews */
  noIndex?: boolean;
  /** JSON-LD structured data object */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Override the locale used for `og:locale` / hreflang. Defaults to the
   * active locale from `LocaleProvider`. See `docs/SoT/localization.md`.
   */
  locale?: Locale;
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  if (lastSpace > maxLength * 0.6) {
    return truncated.slice(0, lastSpace) + "…";
  }
  return truncated + "…";
}

function getAbsoluteImageUrl(imageUrl: string | null | undefined): string {
  if (!imageUrl) return DEFAULT_OG_IMAGE;
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }
  return imageUrl;
}

/**
 * Reusable SEO component for consistent meta tags across pages.
 *
 * Locale handling — see `docs/SoT/localization.md`:
 *   - `og:locale` reflects the active locale (sv_SE / en_US).
 *   - A `hreflang="x-default"` alternate is emitted pointing at the canonical
 *     URL. Per-locale alternates land here when path-based `/en/...` is
 *     introduced in a later phase.
 */
export function Seo({
  title,
  description,
  canonicalUrl,
  ogType = "website",
  image,
  imageAlt,
  noIndex = false,
  jsonLd,
  locale,
}: SeoProps) {
  const { locale: activeLocale } = useLocale();
  const effectiveLocale: Locale = locale ?? activeLocale;
  const ogLocale = OG_LOCALE[effectiveLocale];

  const formattedTitle = title.includes(SITE_NAME)
    ? title
    : `${title} – ${SITE_NAME}`;

  const truncatedDescription = description
    ? truncateText(description, 160)
    : undefined;

  const ogImage = getAbsoluteImageUrl(image);
  const robotsContent = noIndex ? "noindex, nofollow" : "index, follow";

  return (
    <Helmet>
      <title>{formattedTitle}</title>
      {truncatedDescription && (
        <meta name="description" content={truncatedDescription} />
      )}

      <meta name="robots" content={robotsContent} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {canonicalUrl && (
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      )}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={formattedTitle} />
      {truncatedDescription && (
        <meta property="og:description" content={truncatedDescription} />
      )}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      <meta name="twitter:card" content={DEFAULT_TWITTER_CARD} />
      <meta name="twitter:title" content={formattedTitle} />
      {truncatedDescription && (
        <meta name="twitter:description" content={truncatedDescription} />
      )}
      <meta name="twitter:image" content={ogImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default Seo;
